import {
  useState,
  useEffect,
  useRef,
}                             from 'react';
import PropTypes              from 'prop-types';
import { Form }               from 'formsy-semantic-ui-react';
import {
  Label,
  Dimmer,
  Loader,
  Checkbox,
}                             from 'semantic-ui-react';
import { useTranslation }     from 'next-i18next';
import { useRouter }          from 'next/router';
import UIPhoneInput           from 'components/UI/PhoneInput';
import VerificationCodeFields from 'components/Forms/VerificationCode';
import set                    from 'lodash/set';
import EmailInfoBlock         from 'components/Forms/EmailInfoBlock';
import styles                 from './signup-form.module.scss';

import PersonOutlineIcon      from '@material-ui/icons/PersonOutline';
import EmailIcon              from '@material-ui/icons/Email';
import StoreOutlinedIcon      from '@material-ui/icons/StoreOutlined';
import { useProductEvents }   from '../../../hooks';
import { SmartCaptcha }       from '@yandex/smart-captcha';
import getConfig              from 'next/config';

const label = <Label color="red" pointing />;

const SignupForm = ({ loading, register }) => {
  const { t } = useTranslation('common');
  const events = useProductEvents();
  const [ valid, setValid ] = useState(false);
  const [ captchaToken, setCaptchaToken ] = useState(null);
  const [ verificationCodeLoading, setVerificationCodeLoading ] = useState(false);
  const router = useRouter();
  const phoneQueryParam = router.query.phone;
  const [ state, setState ] = useState({
    employee: {
      phone: '',
      email: '',
      name: {
        first: ''
      }
    },
    garage: {
      name: ''
    },
    verificationCode: {
      uuid: '',
      code: ''
    },
    acceptOffer: true
  });
  const [registrationStep, setRegistrationStep] = useState('phone')
  const instanceTimeoutRef = useRef(null);

  useEffect(() => {
    const newState = { ...state };

    if (phoneQueryParam) {
      setState(set(newState, 'employee.phone', phoneQueryParam));
    }

    return () => {
      clearTimeout(instanceTimeoutRef.current)
      setCaptchaToken(null);
    }
  }, [])

  const onChange = (field, value) => {
    const newState = { ...state };

    setState(set(newState, field, value));
  };


  return (
    <div className={styles.component}>
      <Dimmer active={loading} inverted>
        <Loader inverted />
      </Dimmer>

      <Choose>
        <When condition={registrationStep === 'phone'}>
          <Form
            onInvalid={() => { setValid(false) }}
            onValid={() => { setValid(true) }}
            onValidSubmit={(model) => {
              const phoneNumber = model?.employee?.phone;

              if (phoneNumber) {
                events.sendTo(['amplitude'], 'clickToApprovePhone__workshop', { phone: phoneNumber });
                events.sendTo(['ym'], 'clickToApprovePhone');
              }

              setRegistrationStep('code');
            }}
          >
            <Form.Input
              required
              className="new-design no-label"
              autoComplete="off"
              name="employee[name][first]"
              iconPosition="left"
              placeholder={t('signUpForm.yourName')}
              validationErrors={{
                isDefaultRequiredValue: t('formErrors.required')
              }}
              onChange={(event, data) => {
                onChange('employee.name.first', data.value)
              }}
              value={state.employee.name.first}
              errorLabel={label}
            >
              <PersonOutlineIcon />
              <input />
            </Form.Input>

            <Form.Input
              required
              className="new-design no-label"
              autoComplete="off"
              name="garage[name]"
              iconPosition="left"
              placeholder={t('signUpForm.workshopName')}
              validationErrors={{
                isDefaultRequiredValue: t('formErrors.required')
              }}
              onChange={(event, data) => {
                onChange('garage.name', data.value)
              }}
              value={state.garage.name}
              errorLabel={label}
            >
              <StoreOutlinedIcon />
              <input />
            </Form.Input>

            <Form.Input
              required
              autoComplete="off"
              className="email-input new-design no-label"
              placeholder="Email"
              name="employee[email]"
              iconPosition="left"
              validations={{ isEmail: true }}
              validationErrors={{
                isDefaultRequiredValue: t('formErrors.required'),
                isEmail: t('formErrors.isEmail')
              }}
              value={state.employee.email}
              onChange={(event, { value }) => { onChange('employee.email', value) }}
              errorLabel={label}
            >
              <EmailIcon />
              <input />
            </Form.Input>

            <UIPhoneInput
              required
              name="employee[phone]"
              autoComplete="off"
              placeholder={t('auth.phone')}
              validations={{ isValidPhone: true }}
              validationErrors={{
                isDefaultRequiredValue: t('formErrors.required'),
                isValidPhone: t('formErrors.incorrectPhonePattern')
              }}
              onChange={(event, data) => {
                onChange('employee.phone', data.value)
              }}
              value={state.employee.phone}
              errorLabel={label}
              newDesign
              withIcon
            />
            {valid && (
              <SmartCaptcha
                sitekey={getConfig().publicRuntimeConfig.SMART_CAPTCHA_SITE_KEY}
                onSuccess={setCaptchaToken}
              />
            )}

            <div className={styles.actions}>
              <button
                className="z-button button-lg full-width"
                color={'#eee'}
                disabled={!valid || !state.acceptOffer || loading || verificationCodeLoading || !captchaToken}
              >

                {t('auth.signUp')}
              </button>
            </div>

            <div className={styles.accept_offer_form_control}>
              <div
                className={styles.accept_offer_label}
                onClick={() => { onChange('acceptOffer', !state.acceptOffer) }}
              >
                {t('signUpForm.offerLabelPart1')} <a target="_blank" href='/personal_data_processing_agreement.pdf'>{t('signUpForm.agreement')}</a> {t('signUpForm.offerLabelPart2')}.
              </div>
              <Checkbox
                name="acceptOffer"
                checked={state.acceptOffer}
                onChange={(e, { checked }) => { onChange('acceptOffer', checked) }}
              />
            </div>
          </Form>
        </When>
        <When condition={registrationStep === 'code'}>
          <Form
            onInvalid={() => { setValid(false) }}
            onValid={() => { setValid(true) }}
            onValidSubmit={register}
          >
            <Form.Input className="hidden-input" type="hidden" name="employee[name][first]" value={state.employee.name.first} />
            <Form.Input className="hidden-input" type="hidden" name="garage[name]" value={state.garage.name} />
            <Form.Input className="hidden-input" type="hidden" name="employee[email]" value={state.employee.email} />
            <Form.Input className="hidden-input" type="hidden" name="employee[phone]" value={state.employee.phone} />

            <EmailInfoBlock
              phone={state.employee.phone}
              onClickChangeEmail={() => {
                onChange('employee.email', '');
                setRegistrationStep('phone');
                clearTimeout(instanceTimeoutRef.current)
                instanceTimeoutRef.current = setTimeout(() => {
                  const input = document.querySelector('.email-input input');
                  input.focus();
                }, 200);
              }}
            />
            <VerificationCodeFields
              email={state.employee.email}
              onLoading={(value) => { setVerificationCodeLoading(value) }}
              captcha={captchaToken}
              scope="REGISTER_GARAGE"
            />

            <div className={styles.actions}>
              <button
                className="z-button button-lg full-width"
                color={'#eee'}
                disabled={!valid || !state.acceptOffer || loading || verificationCodeLoading}
              >
                {t('auth.signUp')}
              </button>
            </div>
          </Form>
        </When>
      </Choose>
    </div>
  )
};

SignupForm.propTypes = {
  loading: PropTypes.bool,
  register: PropTypes.func.isRequired
};

export default SignupForm;